import { useState, useEffect, useMemo, useRef } from 'react';
import { Send, ArrowUp, MessageSquare, Trash2, Pencil, Check, X, CornerDownRight, ExternalLink, Compass, Search, Plus, ChevronRight, ArrowLeft } from 'lucide-react';
import {
  collection, doc, setDoc, addDoc, deleteDoc, updateDoc, query, orderBy,
  onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { CATEGORY_ICONS } from '../data/icons';
import { useTechniques } from '../hooks/useTechniques';
import { LINGO, matchesLingo, normalizeLingo } from '../data/lingo';
import { topics, LINGO_TERMS, type ForumTopic, type InfoEntry, type TutorialsEntry, type TutorialEntry, type GuideAction } from '../data/topics';
import Spotlights, { CreatorFaces, type LibraryBridge } from './Spotlights';
import CollectionChips from './CollectionChips';
import type { User } from 'firebase/auth';

const ADMIN_UID = 'bZj8y9DApEac3eKOuKV08MKcaDx2';
// Topics ship with the app, so they're all "posted by" Gnosis.
const TOPIC_AUTHOR = 'Gnosis';

// Name to show for the current user.
function displayNameFor(user: User): string {
  return user.displayName || 'User';
}

interface Comment {
  id: string;
  text: string;
  authorUid: string;
  authorName: string;
  createdAt: number;
  editedAt: number;
}

function formatTime(seconds: number): string {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
}

interface ForumProps {
  user: User;
  /** Called when a thread opens/closes so the parent can hide other panels. */
  onThreadOpenChange?: (open: boolean) => void;
  /** Library behaviour for the creator grids (play/star/details). */
  library?: LibraryBridge;
  /** What the guides' live controls do (search, filter, create…), handed down
   *  from the shell that owns that state. */
  guide?: GuideBridge;
}

/** The shell's own actions, for a guide to run for real. Each one lands the
 *  user on the screen where the result shows. */
export interface GuideBridge {
  /** Positions that have techniques for the chosen arts — the filter panel's
   *  own chip list. */
  positions: { id: string; label: string }[];
  /** Library, searched. Clears every other filter first so the search is all
   *  that's narrowing the list. */
  search: (query: string) => void;
  /** Library, filtered to one position and nothing else. */
  filterPosition: (id: string) => void;
  openDiscover: () => void;
  /** Make the collection and open Favorites with it active. */
  createCollection: (name: string) => void;
  /** The + button: opens the add-technique form. */
  openAdd: () => void;
}

// A guide's control sends the user to another tab, which unmounts Home. When
// they tab back, reopen the guide they were reading so it isn't a trapdoor.
let resumeGuide: { topicId: string; itemId: string } | null = null;

// The Home "About" panel: entries ship with the app (see data/topics.ts). A
// 'link' entry goes straight out to an external URL; the others open a detail
// screen over the panel. Comment threads live in `forum_topics/{id}/comments`,
// keyed by the author's uid (one per user).
export default function Forum({ user, onThreadOpenChange, library, guide }: ForumProps) {
  const isAdmin = user.uid === ADMIN_UID;
  const [openTopicId, setOpenTopicId] = useState<string | null>(() => resumeGuide?.topicId ?? null);
  useEffect(() => { if (openTopicId) onThreadOpenChange?.(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openTopic = openTopicId ? topics.find((t) => t.id === openTopicId) ?? null : null;

  const selectTopic = (id: string | null) => {
    setOpenTopicId(id);
    onThreadOpenChange?.(id != null);
  };

  if (openTopic) {
    const back = () => selectTopic(null);
    switch (openTopic.kind) {
      case 'thread':
        return <Thread user={user} isAdmin={isAdmin} topic={openTopic} onBack={back} />;
      case 'info':
        return <InfoPage entry={openTopic} onBack={back} />;
      case 'tutorials':
        return <TutorialsPage entry={openTopic} onBack={back} guide={guide} />;
      case 'spotlights': {
        const Icon = openTopic.icon;
        return (
          <Spotlights
            title={openTopic.title}
            icon={<Icon size={17} />}
            onBack={back}
            library={library}
          />
        );
      }
      // 'link' never opens a screen — the row is an anchor.
    }
  }

  return (
    <div className="forum home-panel">
      <div className="home-panel-body">
        <ul className="forum-topic-list">
          {topics.map((t) => {
            const Icon = t.icon;
            return (
              <li key={t.id}>
                {t.kind === 'link' ? (
                  <a
                    className="forum-topic-row"
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon size={17} className="forum-topic-icon" />
                    <div className="forum-topic-main">
                      <span className="forum-topic-name">{t.title}</span>
                    </div>
                    <span className="forum-topic-meta"><ExternalLink size={14} /></span>
                  </a>
                ) : (
                  <button className="forum-topic-row" onClick={() => selectTopic(t.id)}>
                    <Icon size={17} className="forum-topic-icon" />
                    <div className="forum-topic-main">
                      <span className="forum-topic-name">{t.title}</span>
                    </div>
                    {t.kind === 'thread' && <CommentCount topicId={t.id} />}
                    {t.kind === 'spotlights' && <CreatorFaces />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// Shared chrome for the read-only screens — same header/back as a thread, so
// opening one feels like opening a topic rather than navigating elsewhere.
// Exported so the changelog screens (see Changelog.tsx) match this chrome.
export function DetailPage({ title, onBack, children, icon, action }: {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
  /** The entry's own icon, so an opened screen still carries the mark of the
   *  row it came from. Omitted where the screen is about something else (a
   *  creator page names a person, not the section). */
  icon?: React.ReactNode;
  /** Trailing control for the header row, e.g. a view toggle. */
  action?: React.ReactNode;
}) {
  return (
    <div className="forum-thread home-panel">
      <div className="home-panel-head">
        {/* Bare, like .rp-back on /reels — one back-button idiom in the app.
            A boxed one read as a hole punched in the bar, being darker than it. */}
        <button className="forum-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={19} />
        </button>
        {icon && <span className="forum-head-icon">{icon}</span>}
        <span className="forum-thread-head-title">{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

// A page of copy with an optional way out (currently the Discord invite).
function InfoPage({ entry, onBack }: { entry: InfoEntry; onBack: () => void }) {
  return (
    <DetailPage title={entry.title} onBack={onBack}>
      <div className="forum-thread-topic">
        <p className="forum-thread-body">{entry.body}</p>
        {entry.link && (
          <a
            className="forum-info-link"
            href={entry.link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {entry.link.label}
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </DetailPage>
  );
}

// The app's own control, live, under a guide's copy: the reader does the
// thing instead of reading how. Same components and classes as the real UI,
// so restyling a control restyles it here too. Each one hands off to the
// shell (GuideBridge), which lands the user where the result shows.
function GuideActionControl({ action, guide, remember }: { action: GuideAction; guide: GuideBridge; remember: () => void }) {
  const go = <T,>(fn: (v: T) => void) => (v: T) => { remember(); fn(v); };
  switch (action) {
    case 'search':
      return <GuideSearch onSubmit={go(guide.search)} />;
    case 'positions':
      return (
        <div className="category-filter-bar filter-tone--position">
          {guide.positions.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.id];
            return (
              <button key={cat.id} className="category-filter-chip" onClick={go(guide.filterPosition).bind(null, cat.id)}>
                {Icon && <Icon size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />}
                {cat.label}
              </button>
            );
          })}
        </div>
      );
    case 'athlete':
      return <GuideSearch placeholder="Athlete name or @handle" onSubmit={go(guide.search)} />;
    case 'discover':
      return (
        <button type="button" className="modal-btn modal-btn--add guide-go" onClick={go(guide.openDiscover).bind(null, undefined)}>
          <Compass size={16} /> Open Discover
        </button>
      );
    case 'collection':
      return (
        <CollectionChips
          collections={[]}
          activeId={null}
          onSelect={() => {}}
          onCreate={go(guide.createCollection)}
          onDelete={() => {}}
        />
      );
    case 'add':
      return (
        <button type="button" className="tier-filter-toggle" onClick={guide.openAdd} aria-label="Add a new technique" title="Add a new technique">
          <Plus size={16} />
        </button>
      );
    case 'aliases':
      return <AliasList onPick={go(guide.search)} />;
  }
}

// Everything search will answer to, in two parts: the gym's vocabulary (the
// hand-kept list in data/lingo.ts, whose terms the search widens on) and the
// other names carried by the techniques themselves. Both are read live, so a
// term that finds nothing in today's catalog is left out rather than shown as
// a dead end. Every name here is a button that runs that search for real.
function AliasList({ onPick }: { onPick: (q: string) => void }) {
  const { techniques, loading } = useTechniques();

  // Only the lingo worth reading: an entry needs clips behind it (vocabulary we
  // understand but cannot show is a dead end) and a name that isn't just the
  // label again — "Crucifix, also known as crucifix" is not a row. The label's
  // own wording is dropped from the chips for the same reason, so a row is the
  // everyday name and then the names you would not have guessed.
  const lingo = useMemo(
    () => LINGO
      .filter((e) => techniques.some((t) => matchesLingo(e, t)))
      .map((e) => ({
        label: e.label,
        others: e.terms.filter((t) => normalizeLingo(t) !== normalizeLingo(e.label)),
      }))
      .filter((e) => e.others.length > 0),
    [techniques],
  );

  // The names on the techniques, one row per name rather than per clip: six
  // armbar clips all carry "Juji Gatame" and that is one entry in the lingo.
  const names = useMemo(() => {
    const byName = new Map<string, { name: string; clips: Set<string> }>();
    for (const t of techniques) {
      for (const other of [t.japaneseName, ...(t.aliases ?? [])]) {
        if (!other) continue;
        const key = other.toLowerCase();
        const entry = byName.get(key) ?? { name: other, clips: new Set<string>() };
        entry.clips.add(t.name);
        byName.set(key, entry);
      }
    }
    return [...byName]
      .map(([key, entry]) => ({
        other: entry.name,
        // Un-glossed, the clip's own name is the only label there is.
        term: LINGO_TERMS[key] ?? [...entry.clips].sort()[0],
      }))
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [techniques]);

  if (loading || (lingo.length === 0 && names.length === 0)) return null;

  return (
    <div className="alias-list">
      {lingo.length > 0 && (
        <>
          <div className="alias-list-head">What we call it, and what you might call it</div>
          {lingo.map((e) => (
            <div className="alias-row" key={e.label}>
              <span className="alias-row-name">{e.label}</span>
              <span className="info-modal-names">
                {e.others.map((t) => (
                  <button type="button" className="alias-chip" key={t} onClick={() => onPick(t)}>{t}</button>
                ))}
              </span>
            </div>
          ))}
        </>
      )}
      {names.length > 0 && (
        <>
          <div className="alias-list-head">Names on the clips themselves</div>
          {names.map((r) => (
            <div className="alias-row" key={r.other}>
              <span className="alias-row-name">{r.term}</span>
              <span className="info-modal-names">
                <button type="button" className="alias-chip" onClick={() => onPick(r.other)}>{r.other}</button>
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// The Library search box. Results live in Library, so it submits (Enter or
// the arrow) rather than filtering as you type.
function GuideSearch({ onSubmit, placeholder = 'Find a technique' }: { onSubmit: (q: string) => void; placeholder?: string }) {
  const [q, setQ] = useState('');
  const submit = () => { const t = q.trim(); if (t) onSubmit(t); };
  return (
    <div className="search-input-wrapper guide-search-live">
      <Search size={14} className="search-icon" style={{ left: 10 }} />
      <input
        type="text"
        className="search-input"
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
      />
      <button type="button" className="guide-search-go" onClick={submit} aria-label="Search" disabled={!q.trim()}>
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

// A list of how-tos; tapping one swaps this screen for that tutorial's page,
// and its back button returns here rather than all the way out.
export function TutorialsPage({ entry, onBack, guide }: { entry: TutorialsEntry; onBack: () => void; guide?: GuideBridge }) {
  const [openId, setOpenId] = useState<string | null>(() => {
    const id = resumeGuide?.topicId === entry.id ? resumeGuide.itemId : null;
    resumeGuide = null;
    return id;
  });
  const open: TutorialEntry | null = openId
    ? entry.items.find((i) => i.id === openId) ?? null
    : null;

  if (open) {
    return (
      <DetailPage title={open.title} onBack={() => setOpenId(null)}>
        <div className="forum-thread-topic guide-page">
          <p className="forum-thread-body">{open.body}</p>
          {guide && (
            <div className={`guide-action guide-action--${open.action}`}>
              <GuideActionControl action={open.action} guide={guide} remember={() => { resumeGuide = { topicId: entry.id, itemId: open.id }; }} />
            </div>
          )}
        </div>
      </DetailPage>
    );
  }

  const Icon = entry.icon;
  return (
    <DetailPage title={entry.title} icon={<Icon size={17} />} onBack={onBack}>
      {entry.body && (
        <div className="forum-thread-topic">
          <p className="forum-thread-body">{entry.body}</p>
        </div>
      )}
      <div className="home-panel-body">
        <ul className="forum-topic-list">
          {entry.items.map((i) => {
            const Icon = i.icon;
            return (
              <li key={i.id}>
                <button className="forum-topic-row" onClick={() => setOpenId(i.id)}>
                  <Icon size={17} className="forum-topic-icon" />
                  <div className="forum-topic-main">
                    <span className="forum-topic-name">{i.title}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </DetailPage>
  );
}

// Live comment count for a topic row.
function CommentCount({ topicId }: { topicId: string }) {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    return onSnapshot(collection(db, 'forum_topics', topicId, 'comments'),
      (snap) => setCount(snap.size),
      () => { /* ignore */ },
    );
  }, [topicId]);
  return (
    <span className="forum-topic-meta">
      <MessageSquare size={13} />
      {count ?? 0}
    </span>
  );
}

// Upvote control for a comment (a user's post in a thread). Votes live in
// `forum_topics/{topicId}/comments/{commentId}/votes`, keyed by the voter's uid
// (one vote per user); clicking toggles the current user's vote.
function CommentVote({ topicId, commentId, uid }: { topicId: string; commentId: string; uid: string }) {
  const [count, setCount] = useState(0);
  const [voted, setVoted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return onSnapshot(collection(db, 'forum_topics', topicId, 'comments', commentId, 'votes'),
      (snap) => { setCount(snap.size); setVoted(snap.docs.some((d) => d.id === uid)); },
      () => { /* ignore */ },
    );
  }, [topicId, commentId, uid]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const ref = doc(db, 'forum_topics', topicId, 'comments', commentId, 'votes', uid);
    try {
      if (voted) await deleteDoc(ref);
      else await setDoc(ref, { uid, createdAt: serverTimestamp() });
    } catch (err) {
      console.error('Failed to toggle vote:', err);
    }
    setBusy(false);
  };

  return (
    <button
      className={`forum-vote-btn${voted ? ' forum-vote-btn--on' : ''}`}
      onClick={toggle}
      disabled={busy}
      aria-pressed={voted}
      aria-label={voted ? 'Remove upvote' : 'Upvote'}
      title="Upvote"
    >
      <span>{count}</span>
      <ArrowUp size={14} />
    </button>
  );
}

interface Reply {
  id: string;
  text: string;
  authorUid: string;
  authorName: string;
  createdAt: number;
}

// Replies to a single comment. Stored in
// `forum_topics/{topicId}/comments/{commentId}/replies` (auto-id). Anyone signed
// in can reply, but not twice in a row — you can only reply again once someone
// else has replied after you. Author or admin can delete.
function CommentReplies({ topicId, commentId, user, isAdmin }: {
  topicId: string; commentId: string; user: User; isAdmin: boolean;
}) {
  const [replies, setReplies] = useState<Reply[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  // Can't reply twice in a row: blocked if you authored the most recent reply.
  const lastReply = replies[replies.length - 1];
  const blockedAsLast = !!lastReply && lastReply.authorUid === user.uid;

  useEffect(() => {
    const q = query(
      collection(db, 'forum_topics', topicId, 'comments', commentId, 'replies'),
      orderBy('createdAt', 'asc'),
    );
    return onSnapshot(q,
      (snap) => setReplies(snap.docs.map((d) => ({
        id: d.id,
        text: d.data().text || '',
        authorUid: d.data().authorUid || '',
        authorName: d.data().authorName || 'User',
        createdAt: d.data().createdAt?.seconds || 0,
      }))),
      () => { /* ignore */ },
    );
  }, [topicId, commentId]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending || blockedAsLast) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'forum_topics', topicId, 'comments', commentId, 'replies'), {
        text: t,
        authorUid: user.uid,
        authorName: displayNameFor(user),
        createdAt: serverTimestamp(),
      });
      setText('');
      setOpen(false);
      setExpanded(true);
    } catch (e) {
      console.error('Failed to post reply:', e);
    }
    setSending(false);
  };

  const remove = (id: string) => {
    deleteDoc(doc(db, 'forum_topics', topicId, 'comments', commentId, 'replies', id)).catch(() => {});
  };

  return (
    <div className="forum-replies-wrap">
      {/* Replies start collapsed — tap to reveal the thread. */}
      {replies.length > 0 && (
        <button className="forum-replies-toggle" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          <ChevronRight size={13} className={`forum-replies-chevron${expanded ? ' forum-replies-chevron--open' : ''}`} />
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
        </button>
      )}
      {expanded && replies.length > 0 && (
        <div className="forum-replies">
          {replies.map((r) => (
            <div key={r.id} className="forum-reply">
              <div className="forum-comment-head">
                <span className="forum-comment-author">{r.authorName}</span>
                <span className="forum-comment-time">{formatTime(r.createdAt)}</span>
                {(isAdmin || r.authorUid === user.uid) && (
                  <button className="forum-comment-del" onClick={() => remove(r.id)} aria-label="Delete reply">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <span className="forum-comment-text">{r.text}</span>
            </div>
          ))}
        </div>
      )}
      {/* Reply box: shown when the thread is expanded, or when there are no
          replies yet (so anyone can start one). Hidden if you replied last. */}
      {(expanded || replies.length === 0) && !blockedAsLast && (
        open ? (
          <form className="forum-reply-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input
              className="forum-input"
              placeholder="Write a reply…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
            <button className="forum-send forum-send--sm" type="submit" disabled={!text.trim() || sending}>
              <Send size={14} />
            </button>
          </form>
        ) : (
          <button className="forum-reply-toggle" onClick={() => setOpen(true)}>
            <CornerDownRight size={12} />
            Reply
          </button>
        )
      )}
    </div>
  );
}

function Thread({ user, isAdmin, topic, onBack }: {
  user: User; isAdmin: boolean; topic: ForumTopic; onBack: () => void;
}) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Inline edit of the current user's own comment.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);

  // Anyone can reply, as many times as they like — but not twice in a row. You
  // can only post if you weren't the author of the most recent reply.
  const lastComment = comments[comments.length - 1];
  const blockedAsLast = !!lastComment && lastComment.authorUid === user.uid;

  useEffect(() => {
    const q = query(collection(db, 'forum_topics', topic.id, 'comments'), orderBy('createdAt', 'asc'));
    return onSnapshot(q,
      (snap) => setComments(snap.docs.map((d) => ({
        id: d.id,
        text: d.data().text || '',
        authorUid: d.data().authorUid || '',
        authorName: d.data().authorName || 'User',
        createdAt: d.data().createdAt?.seconds || 0,
        editedAt: d.data().editedAt?.seconds || 0,
      }))),
      () => { /* ignore */ },
    );
  }, [topic.id]);

  useEffect(() => {
    if (comments.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: didInitialScroll.current ? 'smooth' : 'auto' });
    didInitialScroll.current = true;
  }, [comments]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || blockedAsLast) return;
    setInput('');
    setSending(true);
    setError('');
    try {
      // Auto-id doc so a user can post multiple replies over a conversation.
      await addDoc(collection(db, 'forum_topics', topic.id, 'comments'), {
        text,
        authorUid: user.uid,
        authorName: displayNameFor(user),
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('Failed to post comment:', e);
      setInput(text);
      setError('Could not post your comment.');
    }
    setSending(false);
  };

  const removeComment = (id: string) => {
    deleteDoc(doc(db, 'forum_topics', topic.id, 'comments', id)).catch(() => {});
  };

  const startEdit = (c: Comment) => { setEditingId(c.id); setEditText(c.text); setError(''); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (id: string) => {
    const text = editText.trim();
    if (!text) return;
    try {
      await updateDoc(doc(db, 'forum_topics', topic.id, 'comments', id), {
        text,
        editedAt: serverTimestamp(),
      });
      cancelEdit();
    } catch (e) {
      console.error('Failed to edit comment:', e);
      setError('Could not save your edit.');
    }
  };

  return (
    <div className="forum-thread home-panel">
      <div className="home-panel-head">
        <button className="forum-back-btn" onClick={onBack} aria-label="Back to topics">
          <ArrowLeft size={19} />
        </button>
        <span className="forum-thread-head-title">{topic.title}</span>
      </div>

      <div className="forum-thread-topic">
        {topic.body && <p className="forum-thread-body">{topic.body}</p>}
        <span className="forum-thread-author">Posted by {TOPIC_AUTHOR}</span>
      </div>

      <div className="forum-comments">
        {comments.length === 0 ? (
          <div className="forum-empty">Be the first to comment.</div>
        ) : comments.map((c) => (
          <div key={c.id} className="forum-comment">
            <div className="forum-comment-main">
            <div className="forum-comment-content">
              <div className="forum-comment-head">
                <span className="forum-comment-author">{c.authorName}</span>
                <span className="forum-comment-time">
                  {formatTime(c.createdAt)}{c.editedAt ? ' · edited' : ''}
                </span>
                {c.authorUid === user.uid && editingId !== c.id && (
                  <button className="forum-comment-edit-icon" onClick={() => startEdit(c)} aria-label="Edit comment">
                    <Pencil size={15} />
                  </button>
                )}
                {(isAdmin || c.authorUid === user.uid) && (
                  <button className="forum-comment-del" onClick={() => removeComment(c.id)} aria-label="Delete comment">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              {editingId === c.id ? (
                <div className="forum-comment-edit">
                  <textarea
                    className="forum-input forum-comment-edit-input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={2}
                    autoFocus
                  />
                  <div className="forum-comment-edit-actions">
                    <button type="button" className="forum-comment-edit-btn" onClick={cancelEdit} aria-label="Cancel edit">
                      <X size={14} />
                    </button>
                    <button type="button" className="forum-comment-edit-btn forum-comment-edit-btn--save" onClick={() => saveEdit(c.id)} disabled={!editText.trim()} aria-label="Save edit">
                      <Check size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <span className="forum-comment-text">{c.text}</span>
              )}
            </div>
            {editingId !== c.id && (
              <CommentVote topicId={topic.id} commentId={c.id} uid={user.uid} />
            )}
            </div>
            {editingId !== c.id && (
              <CommentReplies topicId={topic.id} commentId={c.id} user={user} isAdmin={isAdmin} />
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {!blockedAsLast && (
        <form className="forum-input-bar" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <input
            className="forum-input"
            placeholder="Add a comment..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button className="forum-send" type="submit" disabled={!input.trim() || sending}>
            <Send size={16} />
          </button>
        </form>
      )}
      {error && <div className="forum-composer-error forum-thread-error">{error}</div>}
    </div>
  );
}
